import React, { useEffect, useState } from "react";

import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { SelectDateRange } from "components/forms";
import { DealGraphConfContent, GroupByMenu, MeasureGraph } from "components/reports";
import { useActionHook } from "hooks";
import { useDealSubAssetsSelector, useDealTitleSelector, useReportsActions, useReportsQueriesSelector } from "store";
import { DateRangeValue } from "store/types";

import { ReactComponent as PortfolioIcon } from "../../assets/icons/portfolio-overview.svg";

export const Graph: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRangeValue<string>>({ value: "fromInception" });

  const { interval, mainParameter, secondaryParameter, exchangeMethod, cumulative, currency, use_accounting_date } =
    useReportsQueriesSelector();
  const subAssets = useDealSubAssetsSelector();
  const dealTitle = useDealTitleSelector();

  const { getMeasureGraphAction } = useReportsActions();

  const {
    action: fetchGraph,
    loading: isGraphLoading,
    data: dealGraph
  } = useActionHook(() => {
    const subAssetNames = subAssets.map((asset) => asset.title);

    return getMeasureGraphAction({
      withinDeal: false,
      // filters: { ["deal_name"]: showChildren ? [dealTitle!, ...subAssetNames] : [dealTitle!] },
      filters: { ["deal_name"]: [dealTitle!, ...subAssetNames] },
      dateRange,
      mainParameter,
      secondaryParameter,
      interval,
      currency,
      exchangeMethod,
      cumulative,
      use_accounting_date,
      groupByGraph: null,
      columnType: "stacked"
    });
  });

  useEffect(() => {
    fetchGraph();
  }, [dateRange, mainParameter, secondaryParameter, interval, exchangeMethod, cumulative, use_accounting_date]);

  // Here is the most awful way how can we decide if there is no data in the graph
  // "any" because the response from the API is not as described in the type
  const dataPoint1: number[] | undefined = (dealGraph as any)?.series[0]?.data;
  const dataPoint2: number[] | undefined = (dealGraph as any)?.series[1]?.data;

  const noData =
    dataPoint1 &&
    dataPoint2 &&
    dataPoint1.length < 2 &&
    dataPoint1[0] === 0 &&
    dataPoint2.length < 2 &&
    dataPoint1[0] === 0;

  return (
    <Stack
      sx={{
        p: "16px",
        gap: "8px",
        borderRadius: "8px",
        bgcolor: "white",
        boxShadow: "0 1px 2px 0 #1212171a",
        flexGrow: 1
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", gap: "10px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071F35" }}>{dealGraph?.title || ""}</Typography>
        <Stack direction="row" sx={{ gap: "10px" }}>
          <SelectDateRange
            formControlProps={{ sx: { minWidth: "200px" } }}
            label=""
            value={dateRange}
            onChange={setDateRange}
          />
          <GroupByMenu>
            <DealGraphConfContent />
          </GroupByMenu>
        </Stack>
      </Stack>
      {isGraphLoading ? (
        <Box sx={{ p: "10px", border: "1px solid #DBE8F5" }}>
          <Skeleton variant="rounded" width="100%" height={400} />
        </Box>
      ) : noData ? (
        <Stack
          sx={{
            alignItems: "center",
            gap: "10px",
            p: "30px 10px 30px 10px",
            bgcolor: "#F6F8FC",
            flexGrow: 1,
            justifyContent: "center"
          }}
        >
          <PortfolioIcon style={{ fill: "#92A7BC", width: "32px", height: "32px" }} />
          <Typography
            sx={{ textAlign: "center", color: "#92A7BC", fontSize: "14px", fontWeight: 400, lineHeight: "16px" }}
          >
            No data available for display. <br /> Please enter measure data to display.
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ p: "10px", border: "1px solid #DBE8F5" }}>
          <MeasureGraph loading={false} graphData={dealGraph} interval={interval} />
        </Box>
      )}
    </Stack>
  );
};
